import Swiper from 'swiper';
import {
	Navigation,
	Pagination,
	Autoplay,
	Keyboard,
	A11y,
	Thumbs,
	FreeMode,
} from 'swiper/modules';

const prefersReducedMotion = () =>
	window.matchMedia &&
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

const CAPTION_MAX_LENGTH = 500;

const limitCaption = ( caption ) =>
	String( caption || '' ).slice( 0, CAPTION_MAX_LENGTH );

/**
 * Full-screen, swipeable image viewer driven by Swiper.
 *
 * One instance is created per gallery so it can reuse the markup rendered by
 * callback.php. Slides are built lazily the first time the lightbox opens.
 */
class GalleryLightbox {
	constructor( gallery, items, options ) {
		this.gallery = gallery;
		this.items = items;
		this.options = options;
		this.modal = gallery.querySelector( '.advanced-gallery__lightbox' );
		this.built = false;
		this.swiper = null;
		this.thumbsSwiper = null;
		this.previouslyFocused = null;

		this.onKeyDown = this.onKeyDown.bind( this );

		if ( this.modal ) {
			this.counter = this.modal.querySelector(
				'.advanced-gallery__lightbox-counter'
			);

			const backdrop = this.modal.querySelector(
				'.advanced-gallery__lightbox-backdrop'
			);
			if ( backdrop ) {
				backdrop.addEventListener( 'click', () => this.close() );
			}

			const closeBtn = this.modal.querySelector(
				'.advanced-gallery__lightbox-close'
			);
			if ( closeBtn ) {
				closeBtn.addEventListener( 'click', () => this.close() );
			}
		}
	}

	build() {
		if ( this.built || ! this.modal ) return;

		const wrapper = this.modal.querySelector(
			'.advanced-gallery__lightbox-swiper .swiper-wrapper'
		);
		const thumbsWrapper = this.modal.querySelector(
			'.advanced-gallery__lightbox-thumbs .swiper-wrapper'
		);

		this.items.forEach( ( item ) => {
			const slide = document.createElement( 'div' );
			slide.className = 'swiper-slide';

			const figure = document.createElement( 'figure' );
			figure.className = 'advanced-gallery__lightbox-figure';

			const img = document.createElement( 'img' );
			img.src = item.full;
			img.alt = item.alt;
			img.className = 'advanced-gallery__lightbox-image';
			img.loading = 'lazy';
			figure.appendChild( img );

			if ( this.options.showCaption && item.caption ) {
				const caption = document.createElement( 'figcaption' );
				caption.className = 'advanced-gallery__lightbox-caption';
				caption.textContent = limitCaption( item.caption );
				figure.appendChild( caption );
			}

			slide.appendChild( figure );
			wrapper.appendChild( slide );

			if ( thumbsWrapper ) {
				const thumbSlide = document.createElement( 'div' );
				thumbSlide.className = 'swiper-slide';
				const thumbImg = document.createElement( 'img' );
				thumbImg.src = item.full;
				thumbImg.alt = '';
				thumbImg.loading = 'lazy';
				thumbSlide.appendChild( thumbImg );
				thumbsWrapper.appendChild( thumbSlide );
			}
		} );

		if ( thumbsWrapper ) {
			this.thumbsSwiper = new Swiper(
				this.modal.querySelector( '.advanced-gallery__lightbox-thumbs' ),
				{
					modules: [ FreeMode, Thumbs, A11y ],
					slidesPerView: 'auto',
					spaceBetween: 8,
					freeMode: true,
					watchSlidesProgress: true,
				}
			);
		}

		const swiperConfig = {
			modules: [ Navigation, Keyboard, A11y, Thumbs ],
			slidesPerView: 1,
			spaceBetween: 0,
			grabCursor: true,
			loop: this.items.length > 1,
			keyboard: { enabled: true },
			a11y: {
				prevSlideMessage: 'Previous image',
				nextSlideMessage: 'Next image',
			},
			navigation: {
				nextEl: this.modal.querySelector(
					'.advanced-gallery__lightbox-swiper .swiper-button-next'
				),
				prevEl: this.modal.querySelector(
					'.advanced-gallery__lightbox-swiper .swiper-button-prev'
				),
			},
		};

		if ( this.thumbsSwiper ) {
			swiperConfig.thumbs = { swiper: this.thumbsSwiper };
		}

		this.swiper = new Swiper(
			this.modal.querySelector( '.advanced-gallery__lightbox-swiper' ),
			swiperConfig
		);

		if ( this.counter ) {
			const updateCounter = () => {
				const current = ( this.swiper.realIndex || 0 ) + 1;
				this.counter.textContent = `${ current } / ${ this.items.length }`;
			};
			this.swiper.on( 'slideChange', updateCounter );
			updateCounter();
		}

		this.built = true;
	}

	open( index ) {
		if ( ! this.modal ) return;

		this.build();
		this.previouslyFocused = document.activeElement;

		this.modal.hidden = false;
		document.body.style.overflow = 'hidden';
		document.addEventListener( 'keydown', this.onKeyDown );

		if ( this.swiper ) {
			// Swiper needs a moment once the modal is visible to measure itself.
			this.swiper.update();
			if ( this.swiper.params.loop ) {
				this.swiper.slideToLoop( index, 0 );
			} else {
				this.swiper.slideTo( index, 0 );
			}
		}

		const closeBtn = this.modal.querySelector(
			'.advanced-gallery__lightbox-close'
		);
		if ( closeBtn ) {
			closeBtn.focus();
		}
	}

	close() {
		if ( ! this.modal || this.modal.hidden ) return;

		this.modal.hidden = true;
		document.body.style.overflow = '';
		document.removeEventListener( 'keydown', this.onKeyDown );

		if ( this.previouslyFocused ) {
			this.previouslyFocused.focus();
		}
		this.previouslyFocused = null;
	}

	onKeyDown( e ) {
		if ( e.key === 'Escape' ) {
			this.close();
			return;
		}

		if ( e.key === 'Tab' ) {
			const focusable = this.modal.querySelectorAll(
				'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
			);
			const visible = Array.from( focusable ).filter(
				( el ) => el.offsetParent !== null
			);
			if ( visible.length === 0 ) return;

			const first = visible[ 0 ];
			const last = visible[ visible.length - 1 ];

			if ( e.shiftKey && document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			} else if ( ! e.shiftKey && document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		}
	}
}

class AdvancedGallery {
	constructor( node ) {
		this.node = node;
		this.layout = node.dataset.layout || 'grid';
		this.lightboxEnabled = node.dataset.lightbox === 'true';

		this.itemEls = Array.from(
			node.querySelectorAll( '.advanced-gallery__item' )
		);

		if ( this.layout === 'slider' ) {
			this.initSlider();
		} else if ( this.layout === 'justified' ) {
			this.initJustified();
		}

		if ( this.lightboxEnabled ) {
			this.initLightbox();
		}
	}

	initSlider() {
		const swiperEl = this.node.querySelector( '.advanced-gallery__swiper' );
		if ( ! swiperEl ) return;

		const data = this.node.dataset;
		const spv = parseInt( data.slidesPerView, 10 ) || 3;
		const spvTablet = parseInt( data.slidesPerViewTablet, 10 ) || 2;
		const spvMobile = parseInt( data.slidesPerViewMobile, 10 ) || 1;
		const spaceBetween = parseInt( data.spaceBetween, 10 ) || 16;
		const autoplay = data.autoplay === 'true' && ! prefersReducedMotion();
		const loop = data.loop === 'true';
		const arrows = data.arrows === 'true';
		const pagination = data.pagination === 'true';

		const config = {
			modules: [ Navigation, Pagination, Autoplay, Keyboard, A11y ],
			slidesPerView: spvMobile,
			spaceBetween,
			grabCursor: true,
			preventClicks: false,
			preventClicksPropagation: false,
			keyboard: { enabled: true },
			a11y: {
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
			},
			breakpoints: {
				768: { slidesPerView: spvTablet, spaceBetween },
				1024: { slidesPerView: spv, spaceBetween },
			},
		};

		if ( loop ) {
			config.loop = true;
		}

		if ( arrows ) {
			config.navigation = {
				nextEl: swiperEl.querySelector( '.swiper-button-next' ),
				prevEl: swiperEl.querySelector( '.swiper-button-prev' ),
			};
		}

		if ( pagination ) {
			config.pagination = {
				el: swiperEl.querySelector( '.swiper-pagination' ),
				clickable: true,
			};
		}

		if ( autoplay ) {
			config.autoplay = {
				delay: parseInt( data.autoplaySpeed, 10 ) || 5000,
				disableOnInteraction: true,
				pauseOnMouseEnter: true,
			};
		}

		this.swiper = new Swiper( swiperEl, config );
	}

	initJustified() {
		const container = this.node.querySelector(
			'.advanced-gallery__justified'
		);
		if ( ! container ) return;

		const gap =
			parseInt(
				getComputedStyle( this.node )
					.getPropertyValue( '--ag-gap' )
					.trim(),
				10
			) || 16;
		const targetRowHeight =
			parseInt( this.node.dataset.targetRowHeight, 10 ) || 240;

		const items = Array.from(
			container.querySelectorAll( '.advanced-gallery__item' )
		);

		const layout = () => {
			const containerWidth = container.clientWidth;
			if ( ! containerWidth ) return;

			let row = [];
			let aspectSum = 0;
			const rows = [];

			items.forEach( ( item ) => {
				const w = parseFloat( item.dataset.width ) || 0;
				const h = parseFloat( item.dataset.height ) || 0;
				const aspect = w && h ? w / h : 1.5;
				item._aspect = aspect;
				row.push( item );
				aspectSum += aspect;

				const projected =
					aspectSum * targetRowHeight + ( row.length - 1 ) * gap;
				if ( projected >= containerWidth ) {
					rows.push( { items: row, aspectSum } );
					row = [];
					aspectSum = 0;
				}
			} );

			if ( row.length ) {
				rows.push( { items: row, aspectSum, last: true } );
			}

			rows.forEach( ( r ) => {
				const gapsTotal = ( r.items.length - 1 ) * gap;
				const available = containerWidth - gapsTotal;
				let rowHeight = available / r.aspectSum;

				// Don't blow up a trailing, partially filled row.
				if ( r.last && rowHeight > targetRowHeight * 1.5 ) {
					rowHeight = targetRowHeight;
				}

				r.items.forEach( ( item ) => {
					const width = item._aspect * rowHeight;
					item.style.flex = 'none';
					item.style.width = `${ Math.floor( width ) }px`;
					item.style.height = `${ Math.floor( rowHeight ) }px`;
				} );
			} );
		};

		layout();

		if ( 'ResizeObserver' in window ) {
			this.resizeObserver = new ResizeObserver( () => layout() );
			this.resizeObserver.observe( container );
		} else {
			window.addEventListener( 'resize', layout );
		}
	}

	initLightbox() {
		const items = this.itemEls.map( ( el ) => ( {
			full: el.dataset.full,
			alt: el.dataset.alt || '',
			caption: el.dataset.caption || '',
		} ) );

		if ( ! items.length ) return;

		const lightbox = new GalleryLightbox( this.node, items, {
			showCaption: this.node.dataset.lightboxCaption === 'true',
		} );

		this.itemEls.forEach( ( el, index ) => {
			el.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				lightbox.open( index );
			} );
		} );
	}
}

const galleries = document.querySelectorAll( '.advanced-gallery' );
galleries.forEach( ( node ) => new AdvancedGallery( node ) );
